/**
 * Posting a lead to the Revenue API intake, and deciding what its answer means
 * for the person who just pressed the button.
 *
 * The endpoint is public and unauthenticated on purpose — there is no key here
 * and none should ever be added to browser code. It stores a contact, a lead
 * row and a `leads_imported` event in one transaction; `twenty-people-sync`
 * puts the contact into Twenty within ten minutes.
 *
 * It notifies nobody. That is why both call sites still post to their existing
 * destination as well: `/contact` to `topgeo-backend`, which emails an admin,
 * and `/lead-generation` to the Google Apps Script that backs the team's sheet.
 * This call is a second write, never a replacement.
 */

import posthog from "posthog-js";
import { bookingContextEnabled, consentedAnonymousDistinctId, prepareBookingContext } from "./booking-context";
import { adAttributionEnabled, adIntakeFields, pageUrlWithoutClickIds } from "./ad-touch";

const INTAKE_URL = "https://revenue-api.topgeo.ai/v1/leads/intake";

/**
 * Closed vocabulary. `leads-by-source` groups by this exact string, so a typo
 * here is a row in a report the founder reads. Anything else is a 422.
 */
export type LeadSource =
  | "website_contact"
  | "website_demo"
  | "website_lead_form"
  | "website_report_cta"
  /** The audit form (E12 P3). Accepted once the Revenue API lists it (P2). */
  | "website_audit";

export type LeadIntakeResult =
  /** 202. The lead is stored. The only result that may fire `lead_captured`. */
  | { status: "stored" }
  /** 422. The visitor must change something; `message` is written to be shown. */
  | { status: "rejected"; message: string }
  /** Anything else: offline, 429, 5xx, CORS. Must NOT block the submission. */
  | { status: "unavailable" }
  /** The decoy was filled. The server answered 202 and stored nothing. */
  | { status: "discarded" };

type LeadIntakeInput = {
  email: string;
  source: LeadSource;
  name?: string;
  company?: string;
  phone?: string;
  message?: string;
  /**
   * The honeypot input's value, passed through from a form that renders one.
   * Omit it on forms that do not — omitted means empty means "a human".
   */
  honeypot?: string;
  /**
   * The submission's id, from `clientEventIdFor()`. Sent only while paid-ad
   * attribution is on; the browser "Lead" event carries the same value.
   */
  clientEventId?: string;
};

/**
 * Current-page UTMs. Impossible to backfill, so captured from the first touch we get.
 * `utm_term` and `utm_id` exist on the intake only from P2 on, so they are sent
 * only while paid-ad attribution is on.
 */
function utmParams(includeContent: boolean, includeAdKeys = false): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === "undefined") return out;
  const params = new URLSearchParams(window.location.search);
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    ...(includeContent ? ["utm_content"] : []),
    ...(includeAdKeys ? ["utm_term", "utm_id"] : []),
  ]) {
    const value = params.get(key);
    if (value) out[key] = value.slice(0, 200);
  }
  return out;
}

/**
 * POST a lead, and classify the answer into the three things a caller can do.
 *
 * Only a 202 returns `"stored"`. Everything that is not a 202 or a 422 returns
 * `"unavailable"` rather than an error, and callers are expected to carry on
 * submitting to their existing destination — this call was added to two forms
 * that already worked, and it must not become a new way for them to fail. A
 * 429 or a bad deploy on this endpoint should cost us a stored row, never a
 * visitor's enquiry.
 *
 * The 422 is the one exception, because it is the only response the visitor can
 * act on: a free-mail address is refused, and its message is written to be
 * shown verbatim. That refusal is not spam filtering. A contact with no company
 * account breaks the CRM sync downstream, so a personal address stored here
 * would fail later, silently, where nobody is looking.
 */
export async function submitLeadIntake(
  input: LeadIntakeInput
): Promise<LeadIntakeResult> {
  try {
    // The helper's timeout is bounded. Context trouble never stops the legacy
    // intake write or changes its response contract.
    const contextEnabled = bookingContextEnabled();
    // Paid-ad attribution (E12 P3): first and last touch, consent and the
    // event id, per the P2 contract (A2). Off, nothing new is sent: the intake
    // refuses unknown fields until P2 is deployed.
    const adEnabled = adAttributionEnabled();
    let adFields = {};
    if (adEnabled) {
      try {
        adFields = {
          ...adIntakeFields(),
          ...(input.clientEventId ? { client_event_id: input.clientEventId.slice(0, 200) } : {}),
        };
      } catch {
        adFields = {};
      }
    }
    let context = null;
    if (contextEnabled) {
      try {
        context = await prepareBookingContext({ getDistinctId: () => consentedAnonymousDistinctId(posthog) });
      } catch {
        context = null;
      }
    }
    // Re-read identity after the bounded mint: a consent/id change must not
    // attach a token minted for the earlier browser state.
    const latestDistinctId = contextEnabled ? consentedAnonymousDistinctId(posthog) : undefined;
    const contextMatchesCurrent = context?.distinctId === latestDistinctId;
    const response = await fetch(INTAKE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: input.email.trim(),
        source: input.source,
        ...(input.name ? { name: input.name.slice(0, 200) } : {}),
        ...(input.company ? { company: input.company.slice(0, 200) } : {}),
        ...(input.phone ? { phone: input.phone.slice(0, 50) } : {}),
        ...(input.message ? { message: input.message.slice(0, 2000) } : {}),
        ...utmParams(contextEnabled, adEnabled),
        ...adFields,
        ...(contextEnabled && latestDistinctId ? { distinct_id: latestDistinctId } : {}),
        ...(contextEnabled && context?.token && contextMatchesCurrent ? { booking_context: context.token } : {}),
        ...(contextEnabled && context?.utmContent && contextMatchesCurrent ? { utm_content: context.utmContent } : {}),
        referrer: typeof document === "undefined" ? "" : document.referrer,
        page_url:
          typeof window === "undefined"
            ? ""
            : adEnabled
              ? pageUrlWithoutClickIds(window.location.href)
              : window.location.href,

        // THE HONEYPOT. Only ever a value a form's hidden decoy input carries.
        //
        // A filled `fax_number` means "bot" to the intake: it answers 202 and
        // writes nothing, deliberately, so a bot cannot learn which field
        // betrayed it. The consequence for us is that a mistake here is
        // invisible — a success response and no lead, forever.
        //
        // Which is the trap: `/lead-generation` has a real, visible, REQUIRED
        // input named `website` (the prospect's own URL). Spreading that
        // form's values into this body would fill the honeypot on every single
        // submission and silently discard every lead it sent, while looking
        // like it worked. That is why this body is built field by field, why no
        // caller passes an object through, and why the decoy arrives as its own
        // named argument rather than as whatever the form happened to call
        // `website`. Do not "simplify" this into a spread.
        fax_number: input.honeypot ?? "",
      }),
    });

    // A filled decoy is answered with a 202 that stored nothing, and that 202
    // is byte-identical to a real one by design — the server will not tell us
    // which it was. We can tell, because we know what we sent. Without this,
    // every bot submission would fire `lead_captured` and `identify()` for a
    // lead that exists nowhere, which is the failure this whole endpoint is
    // shaped to avoid. The request is still made, unchanged, so nothing about
    // the response teaches a bot that it was caught.
    if (input.honeypot) return { status: "discarded" };

    if (response.status === 202) return { status: "stored" };

    if (response.status === 422) {
      const body = await response.json().catch(() => null);
      const message =
        body?.code === "work_email_required" &&
        typeof body?.detail === "string" &&
        body.detail.trim()
          ? body.detail.trim()
          : typeof body?.message === "string"
            ? body.message.trim()
            : "";

      // A 422 only blocks the visitor when it carries a message written to be
      // shown to them — today that is the work-email rule, the one refusal
      // they can actually act on.
      //
      // The same status also covers a malformed body and an unknown `source`,
      // which are OUR bugs. Blocking someone's enquiry and showing them a
      // schema error for a mistake in this file would be the worst outcome
      // available, so an unrecognisable 422 fails open like any other
      // unavailability: the enquiry proceeds, and no event is fired.
      //
      // Unknown fields and other request-shape mistakes are our integration
      // errors, not actionable visitor refusals. Keep those failures open.
      if (message) return { status: "rejected", message };
      return { status: "unavailable" };
    }

    return { status: "unavailable" };
  } catch {
    // Offline, CORS, DNS. Never the visitor's problem.
    return { status: "unavailable" };
  }
}
